import { A, pipe } from "@mobily/ts-belt"
import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonDelete } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { Course } from "@shared/models/course"
import { findCourseDateCustom } from "cc-frontend/lib/find-course-date"
import type { PreparedAction } from "@shared/actions/action-doc-builder"
import { format, parseISO } from "date-fns"
import { compact, map, partial } from "lodash-es"
import type { PatchModel } from "@shared/models/patch"
import Patch from "@shared/models/patch-updater"
import type { Narrative } from "@shared/actions/create-narrative"
import defaultLessonModels from "../../lesson/default-lesson-models"
import defaultPlanbookLessonsActivity from "./default-planbook-lessons-activity"
import defaultPlanbookLessonsContext from "./default-planbook-lessons-context"

interface Payload {
  courses: Array<Course>
  dateString: string
  planbookId: string
}

export function prepareEraseAllLessonsForDay(payload: Payload): PreparedAction {
  return {
    name: PLANBOOK_ERASE_ALL_LESSONS_FOR_DAY.name,
    payload: payload,
    patches: PLANBOOK_ERASE_ALL_LESSONS_FOR_DAY.patches(payload),
    undoPatches: PLANBOOK_ERASE_ALL_LESSONS_FOR_DAY.undoPatches(payload),
    narrative: partial(PLANBOOK_ERASE_ALL_LESSONS_FOR_DAY.narrative, payload),
  }
}
export const PLANBOOK_ERASE_ALL_LESSONS_FOR_DAY = {
  name: "PLANBOOK_ERASE_ALL_LESSONS_FOR_DAY",

  patches(payload: Payload): Array<PatchModel> {
    return compact(
      map(payload.courses, (course) => {
        let courseDate = findCourseDateCustom(course, payload.dateString)
        if (courseDate) {
          return Patch.create("course", course.id, "attributes.calendar.dates", {
            id: courseDate.id,
          })
            .set("embedded", "attributes.cardStackId", null)
            .inc("attributes.calendar.version", 1).patch
        } else {
          return null
        }
      })
    )
  },

  undoPatches(payload: Payload): Array<PatchModel> {
    return compact(
      map(payload.courses, (course) => {
        let courseDate = findCourseDateCustom(course, payload.dateString)
        if (courseDate) {
          return Patch.create("course", course.id, "attributes.calendar.dates", {
            id: courseDate.id,
          })
            .set("embedded", "attributes.cardStackId", courseDate.attributes.cardStackId)
            .inc("attributes.calendar.version", 1).patch
        } else {
          return null
        }
      })
    )
  },

  narrative(payload: Payload, findFn: FindFn, userId: string): Narrative {
    let planbook = findFn("planbook", payload.planbookId)
    let formattedDateString = format(parseISO(payload.dateString), "EEE, LLL. d, yyyy")
    let analyticsEvents = pipe(
      payload.courses,
      A.filter((course) => {
        let courseDate = findCourseDateCustom(course, payload.dateString)
        return (
          courseDate?.attributes.cardStackId !== undefined &&
          courseDate.attributes.cardStackId !== null
        )
      }),
      A.map((course): LessonDelete | null => {
        let courseDate = findCourseDateCustom(course, payload.dateString)
        let cardStackId = courseDate?.attributes.cardStackId
        // We should never hit this because of the filter above ^
        if (cardStackId === undefined || cardStackId === null) return null
        let models = defaultLessonModels(
          { cardStackId: cardStackId, planbookId: payload.planbookId },
          findFn,
          userId
        )
        return {
          ...createBlankEvent(),
          root_id: models.planbook?.id ?? null,
          root_type: "planbook",
          primary_object_id: cardStackId,
          date_plan_scheduled_for: models.dateStringForCardStack,
          primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
          primary_verb: "delete",
          primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
          primary_object_type: "lesson",
          primary_object_specifier: null,
          adverbial_verb: "removing",
          adverbial_object_type: "lesson",
          adverbial_object_specifier: "entire_day",
          adverbial_object_id: null,
          adverbial_object_id_and_ancestor_ids: null,
        }
      }),
      (arr) => compact(arr)
    )

    return {
      title: "Erased An Entire Day Of Lessons",
      titleForAnalytics: "Lesson - Erase Entire Day",
      description: `Erased all the lessons on ${formattedDateString} and removed their lesson histories`,
      context: defaultPlanbookLessonsContext(planbook, payload),
      activity: defaultPlanbookLessonsActivity(payload.planbookId, userId),
      newAnalyticsEvents: analyticsEvents,
    }
  },
}
export default PLANBOOK_ERASE_ALL_LESSONS_FOR_DAY
